import { platformApi } from '@lved/api-gateway';
import { ReactWidget } from '@theia/core/lib/browser';
import { Disposable } from '@theia/core/lib/common/disposable';
import { injectable, postConstruct } from '@theia/core/shared/inversify';

type LicenseOverlayModule = {
    Auth: () => JSX.Element;
    LegalAcceptancePanel: () => JSX.Element;
    LicenseSelect: () => JSX.Element;
    ToolchainSetupProgress: () => JSX.Element;
    useAuthStore: {
        getState: () => { isAuthenticated?: boolean };
        subscribe: (listener: () => void) => () => void;
    };
    useLegalAcceptanceStore: {
        getState: () => { showLegalAcceptance?: boolean };
        subscribe: (listener: () => void) => () => void;
    };
    useLicenseStore: {
        getState: () => { showLicenseSelector?: boolean };
        subscribe: (listener: () => void) => () => void;
    };
    useUIStore: {
        getState: () => { launchScreenOpen?: boolean; projectSelectionRequired?: boolean };
        subscribe: (listener: () => void) => () => void;
    };
};

const LicenseOverlay = ({ module }: { module: LicenseOverlayModule }): JSX.Element => {
    const { Auth, LegalAcceptancePanel, LicenseSelect, ToolchainSetupProgress } = module;
    const isAuthenticated: boolean = module.useAuthStore.getState().isAuthenticated === true;

    if (!isAuthenticated) {
        return (
            <div className="lvgl-license-overlay-auth">
                <Auth />
            </div>
        );
    }

    const showLegalAcceptance: boolean = module.useLegalAcceptanceStore.getState().showLegalAcceptance === true;
    const canShowPostLoginOverlays: boolean = !showLegalAcceptance;

    return (
        <>
            <LegalAcceptancePanel />
            {canShowPostLoginOverlays && <LicenseSelect />}
            {canShowPostLoginOverlays && <ToolchainSetupProgress />}
        </>
    );
};

@injectable()
export class LicenseOverlayHostWidget extends ReactWidget {
    static readonly ID: string = 'widget:lvgl-license-overlay-host';
    static readonly ACTIVE_CLASS: string = 'lvgl-license-overlay-active';

    protected uiModule: LicenseOverlayModule | undefined;
    protected unsubscribeOverlayState?: () => void;
    protected toolchainSetupProgressOpen: boolean = false;

    @postConstruct()
    protected init(): void {
        this.id = LicenseOverlayHostWidget.ID;
        this.title.closable = false;
        this.node.id = 'lvgl-license-overlay';
        this.toDispose.push(
            Disposable.create(() => {
                this.unsubscribeOverlayState?.();
                this.unsubscribeOverlayState = undefined;
            })
        );
        this.loadModule();
    }

    protected async loadModule(): Promise<void> {
        try {
            this.uiModule = (await import('@lved/ui')) as unknown as LicenseOverlayModule;
            this.bindOverlayStateClass();
            this.syncOverlayStateClass();
        } catch {
            this.uiModule = undefined;
        }
        this.update();
    }

    protected bindOverlayStateClass(): void {
        if (!this.uiModule) return;
        this.unsubscribeOverlayState?.();

        const sync = (): void => {
            this.syncOverlayStateClass();
            this.update();
        };
        const unsubs = [
            this.uiModule.useAuthStore.subscribe(sync),
            this.uiModule.useLegalAcceptanceStore.subscribe(sync),
            this.uiModule.useLicenseStore.subscribe(sync),
            this.uiModule.useUIStore.subscribe(sync)
        ];
        const toolchainSetupProgressCallbackId = platformApi.onToolchainSetupProgress((_event, progress) => {
            this.toolchainSetupProgressOpen = progress.isOpen;
            sync();
        });
        this.unsubscribeOverlayState = () => {
            for (const unsubscribe of unsubs) unsubscribe();

            platformApi.removeToolchainSetupProgressListener(toolchainSetupProgressCallbackId);
        };
    }

    protected syncOverlayStateClass(): void {
        if (!this.uiModule) return;
        const isAuthenticated: boolean = this.uiModule.useAuthStore.getState().isAuthenticated === true;
        const legalState = this.uiModule.useLegalAcceptanceStore.getState();
        const showLegalAcceptance: boolean = legalState.showLegalAcceptance === true;
        const showLicenseSelector: boolean = this.uiModule.useLicenseStore.getState().showLicenseSelector === true;
        const uiState = this.uiModule.useUIStore.getState();
        const launchScreenOpen: boolean = uiState.launchScreenOpen === true;
        const projectSelectionRequired: boolean = uiState.projectSelectionRequired === true;
        const isActive: boolean =
            !isAuthenticated ||
            showLegalAcceptance ||
            (showLicenseSelector && !(projectSelectionRequired && launchScreenOpen)) ||
            this.toolchainSetupProgressOpen;
        this.node.classList.toggle(LicenseOverlayHostWidget.ACTIVE_CLASS, isActive);
    }

    protected override render(): JSX.Element {
        if (!this.uiModule) return <></>;
        return <LicenseOverlay module={this.uiModule} />;
    }
}
